import { Info } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type InfoNoteProps = {
  text: ReactNode;
  label?: string;
};

const POPOVER_WIDTH = 256; // px — matches w-64
const VIEWPORT_MARGIN = 12;

/**
 * Inline dated-claim marker: a small ⓘ that reveals its note on hover (desktop)
 * or tap (touch/keyboard). Rendered via portal + fixed positioning (not an
 * absolutely-positioned child) so it can't be clipped by an ancestor's
 * `overflow-hidden` — e.g. the rounded comparison table this lives inside.
 */
export default function InfoNote({
  text,
  label = "Source and date for this claim",
}: InfoNoteProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  function computePosition() {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const popoverHeight = popoverRef.current?.offsetHeight ?? 0;
    const spaceBelow = window.innerHeight - rect.bottom;
    const placeAbove = spaceBelow < popoverHeight + VIEWPORT_MARGIN && rect.top > spaceBelow;

    const left = Math.min(
      Math.max(rect.left + rect.width / 2 - POPOVER_WIDTH / 2, VIEWPORT_MARGIN),
      window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN
    );
    const top = placeAbove ? rect.top - popoverHeight - 8 : rect.bottom + 8;

    setPosition({ top, left });
  }

  // Popover mounts at its natural height on the first commit, so a layout
  // effect can measure it and settle its final position before paint.
  useLayoutEffect(() => {
    if (!open) return;
    computePosition();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    function onOutsideClick(event: MouseEvent) {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    function onReposition() {
      computePosition();
    }

    window.addEventListener("mousedown", onOutsideClick);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      window.removeEventListener("mousedown", onOutsideClick);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [open]);

  return (
    <span
      className="relative inline-block align-middle"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={label}
        className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded-full border border-primary-border bg-primary-dim text-primary-text transition hover:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        aria-haspopup="true"
        aria-controls="info-note-popover"
      >
        <Info className="h-2.5 w-2.5" aria-hidden="true" />
      </button>
      {open &&
        createPortal(
          <div
            id="info-note-popover"
            ref={popoverRef}
            role="note"
            style={{
              position: "fixed",
              top: position?.top ?? -9999,
              left: position?.left ?? -9999,
              width: POPOVER_WIDTH,
              visibility: position ? "visible" : "hidden",
            }}
            className="z-50 rounded-lg border border-border bg-bg-elevated p-3 text-left text-xs font-normal leading-relaxed text-text-muted shadow-lg"
          >
            {text}
          </div>,
          document.body
        )}
    </span>
  );
}
